/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, IconButton, makeStyles, Theme, Tooltip, withStyles } from '@material-ui/core';
import React, { useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import CloseIcon from '@material-ui/icons/Close';
import { formatNumber } from '../../helpers/ChartHelper';
import { SelectedItemProps } from '../MattersByStatusChart';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import MTooltip from '@material-ui/core/Tooltip';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import MapOutlinedIcon from '@material-ui/icons/MapOutlined';
import { RV_CHART_CLASS, RV_CHART_COLORS } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { SearchCriteria } from '../SearchCriteria';
import { DrillDownType } from '../DrillDownType';
import { cdnUrl } from '../../App';
import { CurrentMatterByUSStateParams, MatterState, StateType, useMatterByUSStateQuery } from './hooks/useMatterByUSStateQuery';
import { ChartInfo } from '../../components/ChartInfo';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            overflow: 'hidden',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            // '& svg': {
            // position: 'absolute',
            // top: 0,
            // bottom: 0,
            // left: 0,
            // right: 0,
            // },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        legend: {
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            padding: '2px 15px 8px 15px',
            fontSize: 12,
            color: 'var(--rv-chart-muted)',
        },
        legendSwatch: {
            display: 'inline-block',
            width: 10,
            height: 10,
            marginRight: 6,
            borderRadius: 3,
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        tooltipWrapper: {
            padding: '0px 5px',
        },
        tooltipTitle: {
            textAlign: 'center',
            fontWeight: 600,
            fontSize: '1rem',
        },
        tooltipList: {
            listStyleType: 'none',
            padding: 0,
            '& span': {
                fontWeight: 600,
            },
        },
        mapContainer: {
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
        }
    }),
);

export interface GeoJSONData {
    type:      string;
    bbox:      number[];
    transform: Transform;
    objects:   Objects;
    arcs:      Array<Array<number[]>>;
}

export interface Pokedex {
    type:      string;
    bbox:      number[];
    transform: Transform;
    objects:   Objects;
    arcs:      Array<Array<number[]>>;
}

export interface Objects {
    states: States;
    nation: Nation;
}

export interface Nation {
    type:       string;
    geometries: NationGeometry[];
}

export interface NationGeometry {
    type: Type;
    arcs: Array<Array<number[]>>;
}

export enum Type {
    MultiPolygon = 'MultiPolygon',
    Polygon = 'Polygon',
}

export interface States {
    type:       string;
    geometries: StatesGeometry[];
}

export interface StatesGeometry {
    type:       Type;
    arcs:       Array<Array<number[] | number>>;
    id:         string;
    properties: Properties;
}

export interface Properties {
    name: string;
}

export interface Transform {
    scale:     number[];
    translate: number[];
}

interface USMapChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

export const USMapChart: React.FC<USMapChartProps> = (props) => {
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(true);
    // const [geoData, setGeoData] = useState<GeoJSONData>();
    // const [scale, setScale] = React.useState<number>(1);

    const matterByUSStateParams: CurrentMatterByUSStateParams = {
        entityGuid: props.chartFilter?.entityGuid,
        entityType: props.chartFilter?.entityType,
        responsibility: props.chartFilter?.responsibility,
        showAllRecords: props.showAllRecords,
        filter: props.chartFilter?.filter,
    };

    const matterByCountryQuery = useMatterByUSStateQuery(matterByUSStateParams);

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();
    const mapRefContainer = React.useRef<HTMLDivElement>(null);

    // const geoUrl = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json';
    const geoUrl = `${cdnUrl}/assets/json/us-states.json`;

    React.useEffect(() => {
        fetch(geoUrl)
            .then(response => response.json())
            .then((data: GeoJSONData) => {
                // setGeoData(data);
                setIsLoading(false);
            })
            .catch(error => {
                // tslint:disable-next-line: no-console
                console.error('Error fetching data:', error);
                setIsLoading(false);
            });
    },              []);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(`isUSMapChart`);
        }
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    // tslint:disable-next-line: no-any
    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
        };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Top Referrers by period chart'));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = () => {
        const searchCriterias = new Array<SearchCriteria>();

        if (props.getSelectedItem) {

            // searchCriterias.push({ property: 'Country', operator: 'eq', comparison: 'Australia' });
            searchCriterias.push({ property: 'Country', operator: 'eq', comparison: 'NULL' });

            props.getSelectedItem(
                searchCriterias,
                false,
                // tslint:disable-next-line: max-line-length
                `Current Matters by US State`,
                DrillDownType.matterSummaryDrilldown,
                'Filing Details',
            );
        }
    };

    const handleNoStateClick = () => {
        const searchCriterias = new Array<SearchCriteria>();

        if (props.getSelectedItem) {

            // searchCriterias.push({ property: 'Country', operator: 'eq', comparison: 'Australia' });
            searchCriterias.push({ property: 'NoState', operator: 'eq', comparison: 'NULL' });

            props.getSelectedItem(
                searchCriterias,
                false,
                // tslint:disable-next-line: max-line-length
                `Current Matters by US State`,
                DrillDownType.matterSummaryDrilldown,
                'Filing Details',
            );
        }
    };

    const LightTooltip = withStyles((theme: Theme) => ({
        tooltip: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            border: '1px solid #e8ebf3',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(16, 26, 51, 0.14)',
            fontSize: 11,
        },
    }))(Tooltip);

    const onClickState = (name: string, mStates: MatterState | undefined) => () => {

        if (mStates && props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'State', operator: 'eq', comparison: name });

            props.getSelectedItem(
                searchCriterias,
                false,
                // tslint:disable-next-line: max-line-length
                `Current Matters by US State`,
                DrillDownType.matterSummaryDrilldown,
                'Filing Details',
            );
        }
    };

    if (matterByCountryQuery.loading && matterByCountryQuery.data === undefined) {
        return (
            <div className={`${classes.loadingWrapper} ${RV_CHART_CLASS}`}>
                {chartControllerComponent()}
                <ChartSkeleton variant="card" />
            </div>
        );
    }

    const geoTooltipTitle = (stateName: string, mStates: MatterState | undefined) => {

        const total = mStates ? mStates.stateType.map((sType: StateType) => sType.count).reduce((accumulator, currentValue) => accumulator + currentValue) : 0;

        return (
            <div className={classes.tooltipWrapper}>
                <div className={classes.tooltipTitle}>{`${stateName} (${formatNumber(total)})`}</div>
                {mStates && (
                    <ul className={classes.tooltipList}>
                        {mStates.stateType.map((sType) => (
                            <li>
                                {sType.description}: <span>{formatNumber(sType.count)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    };

    // const handleMoveEnd = (event: any) => {
    // 	console.log('event', event);
    // };

    const { matterStates, totalMattersWithoutState, currentMatters } = matterByCountryQuery.data!.statistics.matter.currentMattersByUsState;

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}`} ref={entireChartRef}>
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<MapOutlinedIcon />} title="Current Matters" />
                <div className={`${classes.total} h2`} onClick={() => handleTotalClick()}>
                    <AnimatedNumber value={currentMatters} format={(amount) => `${formatNumber(amount)}`} />
                </div>
                <div className="h3" onClick={() => handleNoStateClick()}>
                    {`${totalMattersWithoutState} matters with no state`}
                </div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }} ref={mapRefContainer}>
                {isLoading ? (
                    <ChartSkeleton variant="card" />
                ) : (
                        <div className={classes.mapContainer}>
                            <ComposableMap 
                                projection="geoAlbersUsa"
                            >
                                <Geographies geography={geoUrl}>
                                    {({ geographies }) => (
                                        <>
                                            {geographies.map(geo => { 

                                                const stateName: string = geo.properties.name;
                                                const stateCode: string = geo.properties.abbr;

                                                const stateData = matterStates
                                                    ? matterStates.find((mStatus) => mStatus.state.toLowerCase() === stateCode.toLowerCase())
                                                    : undefined;

                                                return (
                                                    <>
                                                        <LightTooltip 
                                                            key={geo.rsmKey} 
                                                            title={geoTooltipTitle(stateName, stateData)} 
                                                            placement={geo.properties.name.toLowerCase() === 'alaska' ? 'top-start' : undefined}
                                                        >
                                                            <Geography
                                                                key={geo.rsmKey}
                                                                stroke="#FFF"
                                                                geography={geo}
                                                                fill="#DDD"
                                                                onClick={onClickState(stateCode, stateData)}
                                                                style={{
                                                                    default: {
                                                                        fill: stateData ? RV_CHART_COLORS.primary : '#dfe5f1',
                                                                        outline: 'none',
                                                                    },
                                                                    hover: {
                                                                        fill: RV_CHART_COLORS.primary,
                                                                        outline: 'none',
                                                                    },
                                                                    pressed: {
                                                                        fill: '#0f4c97',
                                                                        outline: 'none',
                                                                    },
                                                                }}
                                                            />
                                                        </LightTooltip>
                                                    </>
                                                );
                                            })}
                                        </>
                                    )}
                                </Geographies>
                            </ComposableMap>
                        </div>
                    )}
                {/* <div>{`${formatNumber(totalMattersWithoutState)} matters with no State`}</div> */}
            </div>
            <div className={classes.legend}>
                <span>
                    <i className={classes.legendSwatch} style={{ backgroundColor: RV_CHART_COLORS.primary }} />
                    States with matters
                </span>
                <span>
                    <i className={classes.legendSwatch} style={{ backgroundColor: '#dfe5f1' }} />
                    No matters
                </span>
            </div>
        </div>
    );
};
